import CT from '@marketplace-ai/tags';
import { ColDef, DoesFilterPassParams } from 'ag-grid-community';
import { createDateCol } from '~/components/agGrid/columns/dateColumns';
import { createValidCol } from '~/components/agGrid/columns/sharedColumns';
import { CustomSetFilter } from '~/components/agGrid/filters/CustomSetFilter';
import { ACTIONS_COLUMN_BASE, ActionsRenderer, type ActionDef } from '~/components/Table/gridUtil';
import { CertFormValues } from './types';
import { determineActiveStatus } from './utils';

export const createCertCols = ({
  handleEdit,
  handleDelete,
}: {
  handleEdit: (id: number) => void;
  handleDelete: (id: number) => void;
}): ColDef<CertFormValues>[] => {
  return [
    { field: 'id', hide: true },
    createValidCol(),
    {
      // [MCV-1753] Explicit colIds on the three columns that have neither a colId nor a field:
      // ag-grid matches such a column to its previous self only by object identity, so every
      // colDefs rebuild made a new column and maintainColumnOrder appended it after the others.
      colId: 'certName',
      headerName: 'Certification',
      valueGetter: ({ data }) => data?.certName?.label || '',
      filter: {
        component: CustomSetFilter,
        doesFilterPass: ({ model, node }: DoesFilterPassParams) => {
          if (!model || model.length === 0) return true;
          return node.data?.certName && model.includes(node.data.certName.label);
        },
      },
    },
    {
      colId: 'issuer',
      headerName: 'Issuer',
      valueGetter: ({ data }) => data?.issuer?.label || '',
    },
    createDateCol('issueDate', 'Certification Date'),
    createDateCol('expiryDate', 'Expiration Date'),
    {
      colId: 'activeStatus',
      headerName: 'Active',
      valueGetter: ({ data }) => (data?.expiryDate ? determineActiveStatus(data.expiryDate) : ''),
    },
    {
      ...ACTIONS_COLUMN_BASE,
      width: 80,
      cellRenderer: ActionsRenderer,
      cellRendererParams: {
        actions: [
          { type: 'edit', handler: handleEdit, cyTag: CT.certs.table.editBtn },
          { type: 'delete', handler: handleDelete, cyTag: CT.certs.table.deleteBtn },
        ] satisfies ActionDef[],
      },
    },
  ] as ColDef<CertFormValues>[];
};

export const CERTIFICATES = [
  'Basic Life Support (BLS)',
  'Cardiopulmonary Resuscitation (CPR)',
  'Advanced Cardiac Life Support (ACLS)',
  'Neonatal Resuscitation Program (NRP)',
  'Advanced Life Support in Obstetrics (ALSO)',
  'Advanced Trauma Life Support (ATLS)',
  'Pediatric Advanced Life Support (PALS)',
];

export const ISSUER_CERTS = [
  {
    issuer: 'American Heart Association (AHA)',
    certificates: [
      'Basic Life Support (BLS)',
      'Cardiopulmonary Resuscitation (CPR)',
      'Advanced Cardiac Life Support (ACLS)',
      'Pediatric Advanced Life Support (PALS)',
    ],
  },
  {
    issuer: 'American Red Cross',
    certificates: [
      'Basic Life Support (BLS)',
      'Cardiopulmonary Resuscitation (CPR)',
      'Pediatric Advanced Life Support (PALS)',
    ],
  },
  {
    issuer: 'American Academy of Pediatrics (AAP)',
    certificates: ['Neonatal Resuscitation Program (NRP)'],
  },
  {
    issuer: 'American Academy of Family Physicians (AAFP)',
    certificates: ['Advanced Life Support in Obstetrics (ALSO)'],
  },
  {
    issuer: 'American College of Surgeons (ACS)',
    certificates: ['Advanced Trauma Life Support (ATLS)'],
  },
];

export const ISSUERS = [
  'American Heart Association (AHA)',
  'American Red Cross',
  'American Academy of Pediatrics (AAP)',
  'American Academy of Family Physicians (AAFP)',
  'American College of Surgeons (ACS)',
];

export const CERTIFICATES_OPTIONS = CERTIFICATES.map((cert) => ({
  label: cert,
  value: cert,
}));

export const ISSUER_OPTIONS = ISSUERS.map((issuer) => ({
  label: issuer,
  value: issuer,
}));

export const emptyCertForm: CertFormValues = {
  activeFlag: false,
  certName: CERTIFICATES_OPTIONS[0],
  certNum: '',
  expiryDate: '',
  id: undefined,
  issueDate: '',
  issuer: ISSUER_OPTIONS[0],
};
